// Atualizações
import * as React from "react";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import NotificationsIcon from "@mui/icons-material/Notifications";
import MiscellaneousServicesOutlinedIcon from "@mui/icons-material/MiscellaneousServicesOutlined";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import Typography from "@mui/material/Typography";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BugReportIcon from "@mui/icons-material/BugReport";
import {
  Badge,
  Button,
  LinearProgress,
  LinearProgressProps,
} from "@mui/material";
import { useRealTimeHUB } from "../../hooks/realTimeHub";
import { ETypeJob } from "../../interfaces/jobs";
import Modal, { ModalContent } from "../modal";
import { PageJob } from "../../modules/global/pages/jobs/id";
import { downloadReport } from "@/services";

const MAX_HISTORY_SIZE = 20;

function formatTime(timestamp: string): string {
  if (!timestamp) return "";

  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Agora";
  if (diffMins < 60) return `${diffMins}min atrás`;
  if (diffHours < 24) return `${diffHours}h atrás`;
  if (diffDays === 1) return "Ontem";
  if (diffDays < 7) return `${diffDays} dias atrás`;

  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function LinearProgressWithLabel(
  props: LinearProgressProps & { value: number }
) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
      <Box sx={{ width: "100%", mr: 1 }}>
        <LinearProgress
          variant="determinate"
          {...props}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: "rgba(0, 0, 0, 0.08)",
            "& .MuiLinearProgress-bar": {
              borderRadius: 4,
              backgroundColor:
                props.value === 100 ? "success.main" : "primary.main",
            },
          }}
        />
      </Box>
      <Box sx={{ minWidth: 50 }}>
        <Typography
          variant="caption"
          fontWeight={600}
          color={props.value === 100 ? "success.main" : "text.secondary"}
        >
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

export default function Notifications({
  renderTrigger,
}: {
  renderTrigger?: (
    onClick: (event: React.MouseEvent<HTMLElement>) => void,
    ref: React.RefObject<any>,
    badgeContent: number
  ) => React.ReactNode;
}) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const iconRef = React.useRef(null);
  const [openIndex, setOpenIndex] = React.useState(null);
  const [notificationHistory, setNotificationHistory] = React.useState<any[]>(
    []
  );
  const [isBugAnimating, setIsBugAnimating] = React.useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    // Mark all as read when opening
    setNotificationHistory((prev) => {
      const updated = prev.map((n) => ({ ...n, read: true }));
      localStorage.setItem("notificationHistory", JSON.stringify(updated));
      return updated;
    });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { jobsProgress, RealTimeHUB } = useRealTimeHUB();

  React.useEffect(() => {
    const savedHistory = localStorage.getItem("notificationHistory");
    if (savedHistory) {
      try {
        setNotificationHistory(JSON.parse(savedHistory));
      } catch (error) {
        console.error("Erro ao carregar histórico:", error);
      }
    }
  }, []);

  React.useEffect(() => {
    if (jobsProgress.length > 0) {
      setNotificationHistory((prevHistory) => {
        const newHistory: any[] = [];

        jobsProgress.forEach((job) => {
          const existing = prevHistory.find((p) => p.idJob === job.idJob);
          newHistory.push({
            ...job,
            timestamp: (job as any).timestamp || new Date().toISOString(),
            read: existing ? existing.read : false,
          });
        });

        prevHistory.forEach((historicJob) => {
          const stillInProgress = jobsProgress.some(
            (job) => job.idJob === historicJob.idJob
          );
          if (!stillInProgress && historicJob.status === "completed") {
            newHistory.push(historicJob);
          }
        });

        const uniqueJobs = newHistory.reduce((acc, current) => {
          const exists = acc.find((item) => item.idJob === current.idJob);
          if (!exists) {
            acc.unshift({
              ...current,
              timestamp: current.timestamp || new Date().toISOString(),
            });
          } else if (current.progress >= exists.progress) {
            const index = acc.findIndex((item) => item.idJob === current.idJob);
            acc.splice(index, 1);
            acc.unshift({
              ...current,
              timestamp:
                exists.timestamp ||
                current.timestamp ||
                new Date().toISOString(),
            });
          }
          return acc;
        }, [] as any[]);

        const limitedHistory = uniqueJobs.slice(0, MAX_HISTORY_SIZE);

        localStorage.setItem(
          "notificationHistory",
          JSON.stringify(limitedHistory)
        );

        return limitedHistory;
      });
    }
  }, [jobsProgress]);

  React.useEffect(() => {
    if (iconRef.current) {
      RealTimeHUB.on(
        "newJobProgress",
        () => {
          if (!anchorEl) setAnchorEl(iconRef.current);
        },
        "123"
      );
    }
  }, [iconRef.current]);

  const handleItemClick = (index: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenIndex(index);
  };

  const handleTestNotification = () => {
    const testId = `test-${Date.now()}`;
    const newNotification = {
      idJob: testId,
      name: "Teste de Notificação",
      status: "running",
      progress: 0,
      timestamp: new Date().toISOString(),
      type: "TEST",
      details: {},
      read: false,
    };

    setNotificationHistory((prev) => [newNotification, ...prev]);

    // Simula progresso
    let progress = 0;
    const interval = setInterval(() => {
      progress += 20;
      setNotificationHistory((prev) =>
        prev.map((n) =>
          n.idJob === testId
            ? {
                ...n,
                progress,
                status: progress >= 100 ? "completed" : "running",
              }
            : n
        )
      );
      if (progress >= 100) clearInterval(interval);
    }, 800);
  };

  const activeJobsCount = notificationHistory.filter((job) => !job.read).length;

  const handleDownload = (job: any) => {
    downloadReport({
      report: job?.details?.fileName,
    });
  };
  const handleClearHistory = () => {
    setNotificationHistory([]);
    localStorage.removeItem("notificationHistory");
  };

  const sortedNotifications = React.useMemo(() => {
    return [...notificationHistory].sort((a, b) => {
      const dateA = new Date(a.timestamp || 0).getTime();
      const dateB = new Date(b.timestamp || 0).getTime();
      return dateB - dateA;
    });
  }, [notificationHistory]);

  return (
    <Box alignSelf="center" sx={{ width: renderTrigger ? "100%" : "auto" }}>
      <React.Fragment>
        {renderTrigger ? (
          renderTrigger(handleClick, iconRef, activeJobsCount)
        ) : (
          <Box
            sx={{ display: "flex", alignItems: "center", textAlign: "center" }}
          >
            <Tooltip title="Notificações">
              <span>
                <IconButton
                  onClick={handleClick}
                  size="large"
                  ref={iconRef}
                  aria-controls={open ? "account-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                  sx={{
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  <Badge
                    badgeContent={activeJobsCount}
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        minWidth: 20,
                        height: 20,
                        backgroundColor: "#ff5722",
                        color: "#fff",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      },
                    }}
                  >
                    <NotificationsIcon
                      sx={{
                        fontSize: 28,
                        color: "#575B64",
                        transition: "all 0.2s",
                        animation:
                          activeJobsCount > 0 ? "swing 2s ease infinite" : "none",
                        "@keyframes swing": {
                          "0%, 100%": { transform: "rotate(0deg)" },
                          "20%": { transform: "rotate(15deg)" },
                          "40%": { transform: "rotate(-10deg)" },
                          "60%": { transform: "rotate(5deg)" },
                          "80%": { transform: "rotate(-5deg)" },
                        },
                      }}
                    />
                  </Badge>
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}
        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={open}
          onClose={handleClose}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 4px 20px rgba(0,0,0,0.12))",
              mt: 1.5,
              ml: 1.5,
              borderRadius: 2,
              "& .MuiAvatar-root": {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              "&::before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: "50%",
                left: -5,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
              backgroundColor: "rgba(0, 0, 0, 0.02)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>
                Notificações Recentes
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {activeJobsCount > 0
                  ? `${activeJobsCount} em andamento`
                  : "Nenhuma em andamento"}
              </Typography>
            </Box>
            <Box>
              {/* <Tooltip title="Gerar notificação de teste">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    // handleTestNotification();
                    setIsBugAnimating(true);
                    setTimeout(() => {
                      setIsBugAnimating(false);
                    }, 500);
                  }}
                  size="small"
                  sx={{
                    color: "text.secondary",
                    mr: 1,
                    "&:hover": {
                      color: "warning.main",
                      backgroundColor: "rgba(237, 108, 2, 0.08)",
                    },
                  }}
                >
                  <BugReportIcon
                    fontSize="small"
                    sx={{
                      animation: isBugAnimating
                        ? "shake 0.5s ease-in-out"
                        : "none",
                      "@keyframes shake": {
                        "0%": { transform: "rotate(0deg)" },
                        "25%": { transform: "rotate(-20deg)" },
                        "50%": { transform: "rotate(20deg)" },
                        "75%": { transform: "rotate(-20deg)" },
                        "100%": { transform: "rotate(0deg)" },
                      },
                    }}
                  />
                </IconButton>
              </Tooltip> */}

              {sortedNotifications.length > 0 && (
                <Tooltip title="Limpar histórico">
                  <span>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearHistory();
                      }}
                      size="small"
                      sx={{
                        color: "text.secondary",
                        "&:hover": {
                          color: "error.main",
                          backgroundColor: "rgba(244, 67, 54, 0.08)",
                        },
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>
          <List
            sx={{
              width: "100%",
              minWidth: 500,
              maxWidth: 500,
              maxHeight: 600,
              overflow: "auto",
              bgcolor: "background.paper",
              py: 0,
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "rgba(0,0,0,0.05)",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0,0,0,0.2)",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.3)",
                },
              },
            }}
          >
            {sortedNotifications.length === 0 ? (
              <Box
                sx={{
                  p: 6,
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <NotificationsIcon
                  sx={{
                    fontSize: 48,
                    color: "text.disabled",
                    mb: 1,
                  }}
                />
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  color="text.primary"
                >
                  Nenhuma notificação
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Você não tem notificações recentes
                </Typography>
              </Box>
            ) : (
              sortedNotifications.map((job, index) => (
                <React.Fragment key={job.idJob}>
                  {index > 0 && (
                    <Divider variant="inset" component="li" sx={{ ml: 0 }} />
                  )}
                  <ListItem
                    alignItems="center"
                    sx={{
                      py: 2,
                      px: 2.5,
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 56 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor:
                            job.status === "completed"
                              ? "rgba(46, 125, 50, 0.1)"
                              : "rgba(25, 118, 210, 0.1)",
                        }}
                      >
                        {job.status === "completed" ? (
                          <TaskAltIcon
                            sx={{
                              fontSize: "28px",
                              color: "success.main",
                            }}
                          />
                        ) : (
                          <MiscellaneousServicesOutlinedIcon
                            sx={{
                              fontSize: "28px",
                              color: "primary.main",
                              animation: "spin 2s linear infinite",
                              "@keyframes spin": {
                                "0%": { transform: "rotate(0deg)" },
                                "100%": { transform: "rotate(360deg)" },
                              },
                            }}
                          />
                        )}
                      </Box>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            color="text.primary"
                          >
                            {job.name}
                          </Typography>
                          {job.timestamp && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem", fontStyle: "italic" }}
                            >
                              {formatTime(job.timestamp)}
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary={
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                          >
                            <strong>ID:</strong> {job.idJob}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ textTransform: "uppercase" }}
                            >
                              <strong>STATUS:</strong> {job.status}
                            </Typography>
                            <Tooltip title="Clique para ver detalhes">
                              <span>
                                <InfoOutlinedIcon
                                  onClick={(e) => handleItemClick(index, e)}
                                  sx={{
                                    fontSize: "16px",
                                    color:
                                      job.status === "failed"
                                        ? "#d32f2f"
                                        : "#006064",
                                    cursor: "pointer",
                                    "&:hover": {
                                      color:
                                        job.status === "failed"
                                          ? "#b71c1c"
                                          : "#004d40",
                                    },
                                  }}
                                />
                              </span>
                            </Tooltip>
                          </Box>
                          {job.status === "failed" && job.error && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{
                                mt: 0.5,
                                display: "block",
                                fontWeight: 600,
                              }}
                            >
                              ⚠️ Erro: {job.error}
                            </Typography>
                          )}
                          {job.status === "failed" &&
                            job.details?.error &&
                            !job.error && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{
                                  mt: 0.5,
                                  display: "block",
                                  fontWeight: 600,
                                }}
                              >
                                ⚠️ Erro: {job.details.error}
                              </Typography>
                            )}
                          {job.type === ETypeJob.GLOBALGENERATIONREPORT &&
                            job.details.fileName && (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(job);
                                }}
                                sx={{
                                  mt: 1,
                                  textTransform: "none",
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                }}
                              >
                                Baixar Relatório
                              </Button>
                            )}
                          {job.status !== "completed" && (
                            <LinearProgressWithLabel value={job.progress} />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))
            )}
          </List>
        </Menu>
        <Modal
          persistent={true}
          title={`Detalhes da Notificação${
            sortedNotifications[openIndex]?.name
              ? ` - ${sortedNotifications[openIndex].name}`
              : ""
          }`}
          open={openIndex !== null}
          sxModal={{ minWidth: "80%" }}
          onClose={() => setOpenIndex(null)}
        >
          <ModalContent>
            <PageJob
              sxList={{ maxHeight: "86vh", overflow: "auto" }}
              idJob={sortedNotifications[openIndex]?.idJob}
            ></PageJob>
          </ModalContent>
        </Modal>
      </React.Fragment>
    </Box>
  );
}
